import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Brain,
  Sparkles,
  Users,
  Clock,
  ArrowRight,
  Star,
  CheckCircle,
  MessageCircle,
  Target,
  Heart,
  Shield,
  Zap,
  UserPlus,
  BookOpen,
  Award,
  UserCheck,
  ShieldQuestion,
  AlertTriangle,
  Database,
  Lock,
  MonitorSmartphone,
  KeyRound,
  XCircle,
  Headset,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Pricing from "../components/Pricing";
import { config } from "../config";

const LandingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const heroHeight = 800; // Approximate hero section height
      setShowFloatingCTA(scrollY > heroHeight);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // on first render only...
  useEffect(() => {
    if (location.state?.openWaitlist) {
      // clear the flag so it doesn’t re-trigger on subsequent navigations
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openWaitlist, location.pathname, navigate]); // proper dependencies

  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "B.R.I.E.F.S Mode",
      description:
        "Get instant AI-powered mental coaching responses tailored to your specific needs",
      highlight: "Instant Response",
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "B.R.I.E.F a Coach",
      description:
        "Receive AI insights plus personalized guidance from qualified human coaches",
      highlight: "24hr Response",
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Personalized Approach",
      description:
        "Every response is crafted based on your unique situation and mental health journey",
      highlight: "Tailored",
    },
  ];

  const briefSteps = [
    {
      letter: "B",
      word: "Building",
      description:
        "Take a moment to center yourself and focus on your breathing",
    },
    {
      letter: "R",
      word: "Resilience through",
      description:
        "Consider your current emotional state and what you're experiencing",
    },
    {
      letter: "I",
      word: "Interventions",
      description: "Pinpoint the specific challenges or feelings you're facing",
    },
    {
      letter: "E",
      word: "Executive",
      description: "Examine possible solutions and coping strategies",
    },
    {
      letter: "F",
      word: "Function and",
      description: "Concentrate on actionable steps you can take right now",
    },
    {
      letter: "S",
      word: "Stress Management",
      description: "Build resilience and reinforce positive mental patterns",
    },
  ];

  const coachBenefits = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Make a Real Impact",
      description:
        "Help people transform their mental wellness journey with your expertise",
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Flexible Schedule",
      description: "Work on your own terms with our 24-hour response framework",
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Professional Growth",
      description:
        "Develop your coaching skills with our advanced AI-assisted platform",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Secure Platform",
      description:
        "Practice in a safe, structured environment with full support",
    },
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      role: "Working Professional",
      content:
        "B.R.I.E.F.S Coach helped me manage work stress in ways I never thought possible. The AI responses are incredibly insightful!",
      rating: 5,
    },
    {
      name: "Michael R.",
      role: "Student",
      content:
        "Having both AI and human coach options gives me flexibility. The 24-hour coach response feature is a game-changer.",
      rating: 5,
    },
    {
      name: "Emily T.",
      role: "Entrepreneur",
      content:
        "The personalized coaching has transformed how I handle daily challenges. Highly recommend to anyone seeking growth.",
      rating: 5,
    },
  ];

  const faqs = [
    {
      q: "Are my B.R.I.E.F.S. submissions anonymous?",
      a: "Yes. All B.R.I.E.F.S. entries (text or video) are anonymous by default. They are only linked to your account if you choose to share them with a B.R.I.E.F.S. Coach.",
      icon: UserCheck,
    },
    {
      q: "Can coaches see all of my B.R.I.E.F.S.?",
      a: "No. Coaches can only see the submissions you choose to share with them. Everything else stays private to you.",
      icon: ShieldQuestion,
    },
    {
      q: "Is B.R.I.E.F.S. a mental health or therapy app?",
      a: "No. B.R.I.E.F.S. is a performance and stress-management tool, not a clinical service. It is not designed to diagnose, treat, or provide therapy for mental health conditions. If you need clinical support, please contact a licensed professional.",
      icon: MessageCircle,
    },
    {
      q: "What should I do if I’m in crisis?",
      a: "B.R.I.E.F.S. is not an emergency or crisis service. If you are in immediate distress, call 988 (U.S.) or your local emergency number. You can also visit findahelpline.com for international hotlines.",
      icon: AlertTriangle,
    },
    {
      q: "How long are my B.R.I.E.F.S. stored?",
      a: "Your submissions are stored securely for your access. Some features (like the Ventometer) are only available for 24 hours, while others remain until you choose to delete them.",
      icon: Database,
    },
    {
      q: "Who can access my data?",
      a: "Only you (and, if you choose, your coach). We do not sell your data to third parties. Limited technical data may be processed by secure service providers (e.g., cloud storage, analytics).",
      icon: Lock,
    },
    {
      q: "What devices can I use B.R.I.E.F.S. on?",
      a: "B.R.I.E.F.S. works through our web platform and is optimized for mobile and desktop. A native app version is in development.",
      icon: MonitorSmartphone,
    },
    {
      q: "How do I reset my password?",
      a: "Click “Forgot Password” on the login screen and follow the instructions sent to your email.",
      icon: KeyRound,
    },
    {
      q: "Can I cancel my account?",
      a: "Yes. You can cancel anytime through your account settings. If you need assistance, email support@mindbody1.com.",
      icon: XCircle,
    },
    {
      q: "How quickly will support respond?",
      a: "We aim to respond to all inquiries within 1–2 business days.",
      icon: Headset,
    },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSignIn = () => {
    // Navigate to the frontend app's login page based on environment
    window.location.href = `${config.frontendUrl}/login`;
  };

    const handleSignUp = () => {
    // Navigate to the frontend app's login page based on environment
    window.location.href = `${config.frontendUrl}/sign-up`;
  };

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Floating Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-briefs-gradient-radial opacity-10 rounded-full animate-float"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-briefs-gradient-radial opacity-10 rounded-full animate-float float-delay-1"></div>
        <div className="absolute bottom-20 left-1/3 w-64 h-64 bg-briefs-gradient-radial opacity-10 rounded-full animate-float float-delay-2"></div>
      </div>

      {/* Navigation */}
      <nav className="relative z-50 px-6 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* logo */}
          <div className="flex items-center space-x-2">
            <img
              className="h-20 w-auto"
              src="/assets/no-back.png"
              alt="B.R.I.E.F.S Coach Logo"
            ></img>
          </div>

          {/* desktop links */}
          <div className="hidden md:flex items-center space-x-4">
            <Button variant="ghost" onClick={() => scrollToSection("features")}>
              Features
            </Button>
            <Button variant="ghost" onClick={() => scrollToSection("demo")}>
              Demo
            </Button>
            <Button
              variant="ghost"
              onClick={() => scrollToSection("become-coach")}
            >
              Become a Coach
            </Button>
            <Button
              className="bg-briefs-gradient text-white"
              onClick={handleSignIn}
            >
              Sign In
            </Button>
             <Button
              className="bg-briefs-gradient text-white"
              onClick={handleSignUp}
            >
              Sign Up
            </Button>
          </div>

          {/* mobile burger */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* mobile menu drawer */}
        {mobileOpen && (
          <div className="md:hidden absolute top-full inset-x-0 bg-white shadow-md border-t border-gray-100">
            <div className="flex flex-col space-y-2 p-4">
              <Button
                variant="ghost"
                className="w-full text-left"
                onClick={() => {
                  scrollToSection("features");
                  setMobileOpen(false);
                }}
              >
                Features
              </Button>
              <Button
                variant="ghost"
                className="w-full text-left"
                onClick={() => {
                  scrollToSection("demo");
                  setMobileOpen(false);
                }}
              >
                Demo
              </Button>
              <Button
                variant="ghost"
                className="w-full text-left"
                onClick={() => {
                  scrollToSection("become-coach");
                  setMobileOpen(false);
                }}
              >
                Become a Coach
              </Button>
              {/* {role !== 'coach' && ( */}
              {/* <Button variant="ghost" className="w-full text-left" onClick={() => { scrollToSection('become-coach'); setMobileOpen(false); }}>
                  Become a Coach
                </Button> */}
              {/* )} */}
              {/* {loggedIn ? ( */}
              {/* ) : ( */}
              <>
                <Button
                  variant="outline"
                  className="w-full text-left"
                  onClick={() => {
                    handleSignIn();
                    setMobileOpen(false);
                  }}
                >
                  Sign In
                </Button>
              </>
              {/* )} */}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto text-center">
          <div
            className={`transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-briefs-blue/10 to-briefs-purple/10 rounded-full border border-briefs-blue/20 mb-8">
              <Sparkles className="w-4 h-4 text-briefs-blue mr-2" />
              <span className="text-sm font-medium briefs-text-gradient">
                Hybrid Mental Coaching Powered by AI
              </span>
            </div>

            <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight">
              Your Personal
              <span className="block briefs-text-gradient">Mental Coach</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed">
              Experience revolutionary mental health support with our AI-powered
              coaching platform. Get instant insights or connect with
              professional coaches for personalized guidance.
            </p>

            {/* Value highlight bar */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 text-sm">
              <div className="flex items-center bg-white rounded-full px-4 py-2 shadow-md">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                <span className="font-medium">Get Started Today</span>
              </div>
              <div className="flex items-center bg-white rounded-full px-4 py-2 shadow-md">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                <span className="font-medium">No Setup Fees</span>
              </div>
              <div className="flex items-center bg-white rounded-full px-4 py-2 shadow-md">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                <span className="font-medium">Cancel Anytime</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
              <Button
                size="lg"
                className="bg-briefs-gradient hover:opacity-90 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
                onClick={() => scrollToSection("pricing")}
              >
                Get Started Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-briefs-blue text-briefs-blue hover:bg-briefs-blue hover:text-white px-8 py-6 text-lg rounded-xl"
                onClick={() => scrollToSection("demo")}
              >
                Watch Demo
              </Button>
            </div>
          </div>

          {/* Hero Cards */}
          <div
            className={`grid md:grid-cols-2 gap-8 max-w-4xl mx-auto transition-all duration-1000 delay-500 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            <Card className="p-8 bg-gradient-to-br from-briefs-blue/5 to-briefs-purple/5 border-briefs-blue/20 hover:border-briefs-blue/40 transition-all duration-300 hover:scale-105 hover:shadow-xl">
              <CardContent className="p-0">
                <div className="w-16 h-16 bg-briefs-gradient rounded-xl flex items-center justify-center mb-6 mx-auto">
                  <Brain className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 briefs-text-gradient">
                  B.R.I.E.F.S Mode
                </h3>
                <p className="text-gray-600 mb-4">
                  Get instant AI-powered responses to your mental health
                  queries. Fast, reliable, and available 24/7.
                </p>
                <div className="flex items-center text-sm text-briefs-blue font-medium">
                  <Clock className="w-4 h-4 mr-2" />
                  Instant Response
                </div>
              </CardContent>
            </Card>

            <Card className="p-8 bg-gradient-to-br from-briefs-purple/5 to-briefs-blue/5 border-briefs-purple/20 hover:border-briefs-purple/40 transition-all duration-300 hover:scale-105 hover:shadow-xl">
              <CardContent className="p-0">
                <div className="w-16 h-16 bg-briefs-gradient rounded-xl flex items-center justify-center mb-6 mx-auto">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 briefs-text-gradient">
                  B.R.I.E.F a Coach
                </h3>
                <p className="text-gray-600 mb-4">
                  Receive AI insights plus personalized guidance from our
                  qualified professional coaches.
                </p>
                <div className="flex items-center text-sm text-briefs-purple font-medium">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  24hr Expert Response
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Animated Statistics Section */}
      <section className="relative z-10 px-6 py-16 bg-gradient-to-r from-briefs-blue/10 to-briefs-purple/10 border-y border-briefs-blue/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Trusted by Organizations{" "}
              <span className="briefs-text-gradient">Worldwide</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join thousands of professionals who have transformed their mental
              wellness journey
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold briefs-text-gradient mb-2">
                10,000+
              </div>
              <div className="text-gray-600 font-medium">Active Users</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold briefs-text-gradient mb-2">
                500+
              </div>
              <div className="text-gray-600 font-medium">Organizations</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold briefs-text-gradient mb-2">
                50+
              </div>
              <div className="text-gray-600 font-medium">Countries</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold briefs-text-gradient mb-2">
                4.9★
              </div>
              <div className="text-gray-600 font-medium">User Rating</div>
            </div>
          </div>

          {/* Logo carousel placeholder */}
          <div className="mt-16 pt-8 border-t border-briefs-blue/20">
            <p className="text-center text-gray-500 mb-8">
              Trusted by leading organizations
            </p>
            <div className="flex justify-center items-center space-x-12 opacity-60">
              <div className="w-auto h-16 flex items-center justify-center">
                <img
                  className="h-20 w-auto"
                  src="/assets/no-back.png"
                  alt="B.R.I.E.F.S Coach Logo"
                ></img>
              </div>
              <div className="w-auto h-16 flex items-center justify-center">
                <img
                  className="h-20 w-auto"
                  src="/assets/no-back.png"
                  alt="B.R.I.E.F.S Coach Logo"
                ></img>
              </div>
              <div className="w-auto h-16 flex items-center justify-center">
                <img
                  className="h-20 w-auto"
                  src="/assets/no-back.png"
                  alt="B.R.I.E.F.S Coach Logo"
                ></img>
              </div>
              <div className="w-auto h-16 flex items-center justify-center">
                <img
                  className="h-20 w-auto"
                  src="/assets/no-back.png"
                  alt="B.R.I.E.F.S Coach Logo"
                ></img>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Before Pricing */}
      <section className="relative z-10 px-6 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-50 to-blue-50 rounded-full border border-green-200 mb-6">
              <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
              <span className="text-sm font-medium text-green-800">
                Transform Your Organization Today
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Why Leading Organizations Choose{" "}
              <span className="briefs-text-gradient">B.R.I.E.F.S Coach</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Whether you're an organization looking to support your team's
              mental wellness or a practitioner wanting to build your own
              coaching platform, we have the perfect solution.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-2xl font-bold mb-6">For Organizations</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-1 mr-4 flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">
                      Immediate Impact on Team Wellness
                    </h4>
                    <p className="text-gray-600">
                      Provide instant mental health support to your entire
                      workforce with our AI-powered coaching platform.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-1 mr-4 flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">
                      Reduce Burnout & Increase Productivity
                    </h4>
                    <p className="text-gray-600">
                      Our structured approach helps employees manage stress and
                      maintain peak performance.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-1 mr-4 flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Easy Admin Dashboard</h4>
                    <p className="text-gray-600">
                      Manage seats, track usage, and monitor ROI with
                      comprehensive analytics.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold mb-6">For Practitioners</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-1 mr-4 flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">
                      Build Your Branded Platform
                    </h4>
                    <p className="text-gray-600">
                      Create a custom coaching platform with your branding,
                      domain, and unique approach.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-1 mr-4 flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Scale Your Practice</h4>
                    <p className="text-gray-600">
                      Serve more clients with AI assistance while maintaining
                      the personal touch they value.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-1 mr-4 flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Complete Control</h4>
                    <p className="text-gray-600">
                      Manage your own coach team, content library, and client
                      experience.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Risk-free guarantee */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 border border-green-200">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4">
                100% Satisfaction Guarantee
              </h3>
              <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                We're confident you'll love B.R.I.E.F.S Coach. If you're not
                completely satisfied within your first month, we'll work with
                you to make it right.
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  No setup fees
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  Cancel anytime
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  24/7 support
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div id="pricing">
        <Pricing
          onSuccess={(data) => {
            console.log("Purchase successful:", data);
            // Handle successful purchase - could redirect to success page
          }}
        />
      </div>

      {/* What's a B.R.I.E.F Section */}
      <section
        id="brief-explanation"
        className="relative z-10 px-6 py-20 bg-gradient-to-br from-briefs-blue/5 to-briefs-purple/5"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">
              What's a <span className="briefs-text-gradient">B.R.I.E.F</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              We focus on practical, science-based methods to strengthen
              resilience, improve executive function, and manage stress
              effectively. Our approach provides clear tools and structured
              interventions that help individuals think more clearly, adapt
              under pressure, and sustain performance in demanding environments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {briefSteps.map((step, index) => (
              <Card
                key={index}
                className="p-8 bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
              >
                <CardContent className="p-0 text-center">
                  <div className="w-20 h-20 bg-briefs-gradient rounded-full flex items-center justify-center mb-6 mx-auto text-white group-hover:scale-110 transition-transform duration-300">
                    <span className="text-3xl font-bold">{step.letter}</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 briefs-text-gradient">
                    {step.word}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section id="demo" className="relative z-10 px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">
              See{" "}
              <span className="briefs-text-gradient">B.R.I.E.F.S Coach</span> in
              Action
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Watch how our AI-powered platform provides instant mental coaching
              support and connects you with professional coaches.
            </p>
          </div>

          <div className="relative">
            <div className="aspect-video rounded-2xl overflow-hidden border-2 border-briefs-blue/20 shadow-lg">
              <iframe
                src="https://player.vimeo.com/video/1106479382"
                title="B.R.I.E.F.S Coach Demo"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-briefs-gradient rounded-xl flex items-center justify-center mb-4 mx-auto">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold mb-2">Instant AI Responses</h4>
              <p className="text-gray-600">
                Get immediate support when you need it most
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-briefs-gradient rounded-xl flex items-center justify-center mb-4 mx-auto">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold mb-2">Expert Coach Network</h4>
              <p className="text-gray-600">
                Connect with qualified mental health professionals
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-briefs-gradient rounded-xl flex items-center justify-center mb-4 mx-auto">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold mb-2">Personalized Journey</h4>
              <p className="text-gray-600">
                Tailored coaching based on your unique needs
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        className="relative z-10 px-6 py-20 bg-gradient-to-br from-gray-50 to-white"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">
              Why Choose{" "}
              <span className="briefs-text-gradient">B.R.I.E.F.S Coach</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our innovative approach combines cutting-edge AI technology with
              human expertise to provide unparalleled mental health support.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="p-8 bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
              >
                <CardContent className="p-0 text-center">
                  <div className="w-16 h-16 bg-briefs-gradient rounded-xl flex items-center justify-center mb-6 mx-auto text-white group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <div className="inline-flex items-center px-3 py-1 bg-briefs-gradient/10 rounded-full mb-4">
                    <span className="text-sm font-medium briefs-text-gradient">
                      {feature.highlight}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Become a Coach Section */}
      <section
        id="become-coach"
        className="relative z-10 px-6 py-20 bg-gradient-to-br from-briefs-purple/5 to-briefs-blue/5"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-briefs-purple/10 to-briefs-blue/10 rounded-full border border-briefs-purple/20 mb-8">
              <UserPlus className="w-4 h-4 text-briefs-purple mr-2" />
              <span className="text-sm font-medium briefs-text-gradient">
                Join Our Team
              </span>
            </div>
            <h2 className="text-5xl font-bold mb-6">
              Become a{" "}
              <span className="briefs-text-gradient">B.R.I.E.F.S Coach</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join our network of qualified mental performance professionals and
              help transform lives through innovative AI-assisted coaching.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-8">
                {coachBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-briefs-gradient rounded-lg flex items-center justify-center text-white flex-shrink-0">
                      {benefit.icon}
                    </div>
                    <div>
                      <h4 className="text-xl font-bold mb-2">
                        {benefit.title}
                      </h4>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-briefs-gradient rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Award className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-3xl font-bold mb-4 briefs-text-gradient">
                  Ready to Make an Impact?
                </h3>
                <p className="text-gray-600 mb-8">
                  We’re seeking coaches who bring both expertise and innovation
                  to the table, and professionals who can blend science with
                  practical coaching to help people strengthen resilience and
                  performance.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center text-gray-600">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span>
                    Background in performance psychology or mental health
                    (minimum masters level)
                  </span>
                </div>
                <div className="flex items-center text-gray-600">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span>2+ years of applied coaching experience</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span>Interest in technology-driven coaching tools</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <span>Flexible schedule and strong communication skills</span>
                </div>
              </div>

              <Button
                type="button"
                className="w-full mt-8 bg-briefs-gradient hover:opacity-90 text-white py-6 text-lg rounded-xl"
                onClick={() => navigate("/coach-register")}
              >
                Apply to Become a Coach
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">
              What Our <span className="briefs-text-gradient">Users Say</span>
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from people who've transformed their mental wellness
              journey with us.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                className="p-8 bg-gradient-to-br from-white to-gray-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <CardContent className="p-0">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 text-yellow-400 fill-current"
                      />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <p className="font-semibold briefs-text-gradient">
                      {testimonial.name}
                    </p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* {!loggedIn && ( */}
      <section className="relative z-10 px-6 py-20 bg-briefs-gradient">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-5xl font-bold mb-6">
            Ready to Transform Your Mental Wellness?
          </h2>
          <p className="text-xl mb-12 opacity-90">
            Join thousands of users who have already started their journey to
            better mental health with B.R.I.E.F.S Coach.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button
              size="lg"
              className="bg-white text-briefs-blue hover:bg-gray-100 px-8 py-6 text-lg rounded-xl font-semibold"
              onClick={() => scrollToSection("pricing")}
            >
              Get Started Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <div className="flex items-center text-white/90">
              <CheckCircle className="w-5 h-5 mr-2" />
              <span>No commitment required</span>
            </div>
          </div>
        </div>
      </section>
      {/* )} */}

      {/* FAQ Section */}
      <section
        id="faqs"
        className="relative z-10 px-6 py-20 bg-gradient-to-br from-briefs-blue/5 to-briefs-purple/5"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-briefs-blue/10 to-briefs-purple/10 rounded-full border border-briefs-blue/20 mb-6">
              <Sparkles className="w-4 h-4 text-briefs-blue mr-2" />
              <span className="text-sm font-medium briefs-text-gradient">
                Everything You Need To Know
              </span>
            </div>
            <h2 className="text-5xl font-bold mb-4">
              Frequently Asked{" "}
              <span className="briefs-text-gradient">Questions</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transparent, privacy-first, and built to support your performance
              and stress management.
            </p>
          </div>

          {/* show 5 by default; toggle to all */}
          <div className="grid lg:grid-cols-1 gap-6 mx-20">
            {(showAllFaqs ? faqs : faqs.slice(0, 5)).map((item, idx) => {
              // idx here is fine for keys since q is unique; openFaq uses same 0..N indexing
              const isOpen = openFaq === idx;
              const answerId = `faq-panel-${idx}`;
              const buttonId = `faq-trigger-${idx}`;
              return (
                <div
                  key={item.q}
                  className={
                    [
                      "rounded-2xl",
                      "border border-briefs-blue/20",
                      "bg-white/40",
                      "backdrop-blur-md",
                      "supports-[backdrop-filter]:bg-white/30",
                      "shadow-sm hover:shadow-xl",
                      "hover:border-briefs-blue/40",
                      "transition-all duration-300",
                    ].join(" ") +
                    (isOpen ? " border-briefs-blue/40 shadow-lg" : "")
                  }
                >
                  <button
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={answerId}
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full flex items-center justify-between gap-4 text-left p-5 md:p-6"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-briefs-gradient text-white flex items-center justify-center">
                        {item.icon && <item.icon className="w-5 h-5" />}
                      </div>
                      <h3 className="text-lg md:text-xl font-semibold text-gray-900">
                        {item.q}
                      </h3>
                    </div>
                    <span
                      className={`ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-briefs-blue/30 text-briefs-blue transition-transform duration-300 ${
                        isOpen ? "rotate-90" : ""
                      }`}
                      aria-hidden="true"
                    >
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </button>

                  <div
                    id={answerId}
                    role="region"
                    aria-labelledby={buttonId}
                    className={`px-5 md:px-6 pb-6 overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
                      isOpen ? "opacity-100" : "opacity-0"
                    }`}
                    style={{ maxHeight: isOpen ? 500 : 0 }}
                  >
                    <p className="text-gray-700 leading-relaxed">
                      {item.q.includes("crisis") ? (
                        <>
                          B.R.I.E.F.S. is not an emergency or crisis service. If
                          you are in immediate distress, call{" "}
                          <strong>988</strong> (U.S.) or your local emergency
                          number. You can also visit{" "}
                          <a
                            className="text-briefs-blue underline decoration-briefs-blue/40 underline-offset-4 hover:opacity-80"
                            href="https://findahelpline.com"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            findahelpline.com
                          </a>{" "}
                          for international hotlines.
                        </>
                      ) : item.q.includes("cancel my account") ? (
                        <>
                          Yes. You can cancel anytime through your account
                          settings. If you need assistance, email{" "}
                          <a
                            className="text-briefs-blue underline decoration-briefs-blue/40 underline-offset-4 hover:opacity-80"
                            href="mailto:support@mindbody1.com"
                          >
                            support@mindbody1.com
                          </a>
                          .
                        </>
                      ) : (
                        item.a
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Show more / less toggle */}
          {faqs.length > 5 && (
            <div className="mt-12 text-center">
              <Button
                className="bg-briefs-gradient text-white px-8 py-6 text-lg rounded-xl hover:opacity-90"
                onClick={() => setShowAllFaqs(!showAllFaqs)}
                aria-expanded={showAllFaqs}
                aria-controls="faqs"
              >
                {showAllFaqs ? "Show Less" : "Show More"}
                <ArrowRight
                  className={`ml-2 w-5 h-5 transition-transform ${
                    showAllFaqs ? "rotate-180" : ""
                  }`}
                />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-12 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <img
                className="h-20 w-auto"
                src="/assets/no-back.png"
                alt="B.R.I.E.F.S Coach Logo"
              ></img>
            </div>
            <div className="flex items-center space-x-8 text-gray-400">
              <a href="/privacy" className="hover:text-white transition-colors">
                Privacy
              </a>
              <button className="hover:text-white transition-colors">
                Terms
              </button>
              <button className="hover:text-white transition-colors">
                Support
              </button>
              <button className="hover:text-white transition-colors">
                Contact
              </button>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 B.R.I.E.F.S Coach. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Waitlist Form Modal */}
      {/* <WaitlistForm
        isOpen={showWaitlist}
        onClose={() => setShowWaitlist(false)}
      /> */}

      {/* Floating CTA Button */}
      <div
        className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
          showFloatingCTA
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10 pointer-events-none"
        }`}
      >
        <Button
          size="lg"
          className="bg-briefs-gradient hover:opacity-90 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all animate-pulse"
          onClick={() => scrollToSection("pricing")}
        >
          <Star className="w-5 h-5 mr-2" />
          Get Started
        </Button>
      </div>
    </div>
  );
};

export default LandingPage;
